import { useState } from 'react';
import { useApp } from '@/context/AppContext';

function fmtDate(d) {
  if (!d) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  try {
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d);
  }
}

function chemRow(label, value, range) {
  return (
    <div className="wo-chem-row">
      <span className="wo-chem-label">{label}</span>
      <span className="wo-chem-value">{value ?? '—'}</span>
      <span className="wo-chem-range">{range}</span>
    </div>
  );
}

function checkbox(label) {
  return (
    <label className="wo-check">
      <span className="wo-box" />
      <span>{label}</span>
    </label>
  );
}

export default function WorkOrderModal({ data }) {
  const { stops, custById, closeModal, companyProfile, showToast } = useApp();
  const stop = data?.stopId ? stops.find(s => s.id === data.stopId) : null;
  const c = data?.custId ? custById(data.custId) : (stop ? custById(stop.custId) : null);

  const [downloading, setDownloading] = useState(false);

  if (!c) return null;

  const woNumber = `WO-${(stop?.id || c.id || Date.now()).toString().slice(-6).padStart(6, '0')}`;
  const serviceDate = stop?.date || new Date().toISOString().slice(0, 10);
  const tech = stop?.tech || c.tech || 'Unassigned';
  const chem = stop?.chem || c.chem || {};

  // P6.1 — Replace window.print() with a real jspdf download.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const W = doc.internal.pageSize.getWidth();
      const M = 48; // margin
      let y = M;

      // Header
      doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
      doc.text(companyProfile?.name || 'PainterOps', M, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.setTextColor(110);
      doc.text('Pool Service Work Order', M, y + 16);
      doc.setTextColor(0); doc.setFontSize(10);
      const metaX = W - M;
      doc.text(`WO #: ${woNumber}`, metaX, y, { align: 'right' });
      doc.text(`Date: ${fmtDate(serviceDate)}`, metaX, y + 14, { align: 'right' });
      doc.text(`Tech: ${tech}`, metaX, y + 28, { align: 'right' });
      y += 50;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;

      // Section helper
      const section = (title) => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(0);
        doc.text(title.toUpperCase(), M, y); y += 14;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      };
      const row = (label, value) => {
        doc.setFont('helvetica', 'bold'); doc.text(`${label}:`, M, y);
        doc.setFont('helvetica', 'normal'); doc.text(String(value ?? '—'), M + 100, y);
        y += 14;
      };

      section('Customer');
      row('Name', c.name);
      row('Phone', c.phone || '—');
      row('Address', c.addr || '—');
      row('Email', c.email || '—');
      row('Gate Code', c.gateCode || '—');
      y += 6;

      section('Pool Details');
      row('Volume',     `${c.poolGal || '—'} gal`);
      row('Controller', c.controller || 'None');
      row('Pets',       c.pets || 'None');
      row('Plan',       `$${c.mrr || 0}/mo`);
      y += 6;

      section('Services Performed');
      ['Skim surface','Brush walls','Vacuum','Empty baskets','Backwash filter','Clean filter cartridge','Equipment inspection','Tile cleaning'].forEach(s => {
        doc.rect(M, y - 9, 9, 9);
        doc.text(s, M + 16, y); y += 14;
      });
      y += 6;

      section('Water Chemistry');
      [
        ['Free Chlorine', chem.cl,  '1.0 – 4.0 ppm'],
        ['pH',            chem.ph,  '7.2 – 7.8'],
        ['Alkalinity',    chem.alk, '80 – 120 ppm'],
        ['CYA',           chem.cya, '30 – 50 ppm'],
      ].forEach(([l, v, r]) => {
        doc.setFont('helvetica', 'bold'); doc.text(`${l}:`, M, y);
        doc.setFont('helvetica', 'normal'); doc.text(String(v ?? '—'), M + 110, y);
        doc.setTextColor(140); doc.text(`(${r})`, M + 170, y); doc.setTextColor(0);
        y += 14;
      });
      y += 6;

      section('Chemicals Added');
      ['Liquid Chlorine','Muriatic Acid','Soda Ash / Borax','Stabilizer','Other'].forEach(label => {
        doc.text(`${label}: ${'_'.repeat(28)}`, M, y); y += 14;
      });
      y += 6;

      section('Notes / Issues Found');
      const noteLines = doc.splitTextToSize(stop?.notes || ' ', W - 2 * M);
      doc.text(noteLines, M, y); y += Math.max(60, noteLines.length * 12);
      y += 6;

      section('Signatures');
      doc.text('_____________________________', M, y + 30);
      doc.text('Technician Signature', M, y + 44);
      doc.text('_____________________________', W / 2, y + 30);
      doc.text('Customer Signature (optional)', W / 2, y + 44);
      y += 60;

      // Footer
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text(`Generated ${fmtDate(new Date())} · PainterOps`, W / 2, doc.internal.pageSize.getHeight() - 24, { align: 'center' });

      doc.save(`${woNumber}-${c.name.replace(/\s+/g, '_')}.pdf`);
      showToast?.('Work order downloaded ✓');
    } catch (e) {
      console.error(e);
      showToast?.(`PDF generation failed: ${e.message || e}`, false);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="modal wo-modal" style={{ maxWidth: 760 }}>
      <div className="modal-head wo-no-print">
        <span className="modal-title">Work Order — {c.name}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Generating…' : '📄 Download PDF'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
        </div>
      </div>

      <div className="modal-body wo-printable" style={{ background: '#fff', color: '#111' }}>
        {/* Header */}
        <div className="wo-header">
          <div>
            <div className="wo-brand">PainterOps</div>
            <div className="wo-brand-sub">Pool Service Work Order</div>
          </div>
          <div className="wo-meta">
            <div><strong>WO #:</strong> {woNumber}</div>
            <div><strong>Date:</strong> {fmtDate(serviceDate)}</div>
            <div><strong>Tech:</strong> {tech}</div>
          </div>
        </div>

        {/* Customer block */}
        <div className="wo-section">
          <div className="wo-section-title">Customer</div>
          <div className="wo-grid">
            <div><strong>Name:</strong> {c.name}</div>
            <div><strong>Phone:</strong> {c.phone || '—'}</div>
            <div className="wo-col-span-2"><strong>Address:</strong> {c.addr}</div>
            <div><strong>Email:</strong> {c.email || '—'}</div>
            <div><strong>Gate Code:</strong> {c.gateCode || '—'}</div>
          </div>
        </div>

        {/* Pool details */}
        <div className="wo-section">
          <div className="wo-section-title">Pool Details</div>
          <div className="wo-grid">
            <div><strong>Volume:</strong> {c.poolGal || '—'} gal</div>
            <div><strong>Controller:</strong> {c.controller || 'None'}</div>
            <div><strong>Pets:</strong> {c.pets || 'None'}</div>
            <div><strong>Plan:</strong> ${c.mrr || 0}/mo</div>
          </div>
        </div>

        {/* Services performed */}
        <div className="wo-section">
          <div className="wo-section-title">Services Performed</div>
          <div className="wo-checks">
            {checkbox('Skim surface')}
            {checkbox('Brush walls')}
            {checkbox('Vacuum')}
            {checkbox('Empty baskets')}
            {checkbox('Backwash filter')}
            {checkbox('Clean filter cartridge')}
            {checkbox('Equipment inspection')}
            {checkbox('Tile cleaning')}
          </div>
        </div>

        {/* Chemistry readings */}
        <div className="wo-section">
          <div className="wo-section-title">Water Chemistry</div>
          <div className="wo-chem">
            {chemRow('Free Chlorine', chem.cl,  '1.0 – 4.0 ppm')}
            {chemRow('pH',            chem.ph,  '7.2 – 7.8')}
            {chemRow('Alkalinity',    chem.alk, '80 – 120 ppm')}
            {chemRow('CYA',           chem.cya, '30 – 50 ppm')}
          </div>
        </div>

        {/* Chemicals added */}
        <div className="wo-section">
          <div className="wo-section-title">Chemicals Added</div>
          <div className="wo-grid">
            <div><strong>Liquid Chlorine:</strong> ____________ gal</div>
            <div><strong>Muriatic Acid:</strong> ____________ oz</div>
            <div><strong>Soda Ash / Borax:</strong> ____________ lbs</div>
            <div><strong>Stabilizer:</strong> ____________ lbs</div>
            <div className="wo-col-span-2"><strong>Other:</strong> _________________________________________</div>
          </div>
        </div>

        {/* Notes */}
        <div className="wo-section">
          <div className="wo-section-title">Notes / Issues Found</div>
          <div className="wo-notes-box">
            {stop?.notes || ' '}
          </div>
        </div>

        {/* Signatures */}
        <div className="wo-section wo-signatures">
          <div className="wo-sig">
            <div className="wo-sig-line">_________________________</div>
            <div className="wo-sig-label">Technician Signature</div>
          </div>
          <div className="wo-sig">
            <div className="wo-sig-line">_________________________</div>
            <div className="wo-sig-label">Customer Signature (optional)</div>
          </div>
        </div>

        {/* Footer */}
        <div className="wo-footer">
          Generated {fmtDate(new Date())} · PainterOps
        </div>
      </div>
    </div>
  );
}
